import { Navigate, createBrowserRouter } from "react-router-dom";
import { Center, Text } from "@mantine/core";
import ProtectedRoute from "./ProtectedRoute";
import RootRedirect from "./RootRedirect";
import { hiddenFeatures } from "@/lib/hidden-features";

import AuthLayout from "@/pages/auth/AuthLayout";
import Login from "@/pages/auth/Login";
import SignUp from "@/pages/auth/SignUp";
import VerifyEmail from "@/pages/auth/VerifyEmail";
import ForgotPassword from "@/pages/auth/ForgotPassword";

import Eula from "@/pages/documents/Eula";
import PrivacyPolicy from "@/pages/documents/PrivacyPolicy";
import TermsAndConditions from "@/pages/documents/TermsAndConditions";

import AppLayout from "@/pages/app/AppLayout";
import Home from "@/pages/app/Home";
import Settings from "@/pages/app/Settings";
import PathLayout from "@/pages/app/path/PathLayout";
import NewPaths from "@/pages/app/path/NewPaths";
import InProgress from "@/pages/app/path/InProgress";
import Completed from "@/pages/app/path/Completed";
import PathFinderLayout from "@/pages/app/path-finder/PathFinderLayout";
import Shortcut from "@/pages/app/path-finder/Shortcut";
import Atlas from "@/pages/app/path-finder/Atlas";
import EngagementCenterLayout from "@/pages/app/engagement-center/EngagementCenterLayout";
import Checklist from "@/pages/app/engagement-center/Checklist";
import HealthSummary from "@/pages/app/engagement-center/HealthSummary";
import Bookmarks from "@/pages/app/engagement-center/Bookmarks";
import KnowledgeCheck from "@/pages/app/engagement-center/KnowledgeCheck";

function NotFound() {
  return (
    <Center h="100vh">
      <Text size="lg">404 — page not found</Text>
    </Center>
  );
}

// URL paths are unchanged from the TanStack file-based tree (INT-38 spec §5.3).
export const router = createBrowserRouter([
  { path: "/", element: <RootRedirect /> },
  {
    path: "/auth",
    element: <AuthLayout />,
    children: [
      { index: true, element: <Navigate to="/auth/login" replace /> },
      { path: "login", element: <Login /> },
      { path: "sign-up", element: <SignUp /> },
      { path: "verify-email", element: <VerifyEmail /> },
      { path: "forgot-password", element: <ForgotPassword /> },
    ],
  },
  {
    path: "/documents",
    children: [
      { path: "eula", element: <Eula /> },
      { path: "privacy-policy", element: <PrivacyPolicy /> },
      { path: "terms-and-conditions", element: <TermsAndConditions /> },
    ],
  },
  {
    element: <ProtectedRoute />,
    children: [
      {
        path: "/app",
        element: <AppLayout />,
        children: [
          { index: true, element: <Navigate to="/app/home" replace /> },
          { path: "home", element: <Home /> },
          { path: "settings", element: <Settings /> },
          {
            path: "path",
            element: <PathLayout />,
            children: [
              { index: true, element: <NewPaths /> },
              { path: "in-progress", element: <InProgress /> },
              { path: "completed", element: <Completed /> },
            ],
          },
          {
            path: "path-finder",
            element: <PathFinderLayout />,
            children: [
              { index: true, element: <Shortcut /> },
              ...(hiddenFeatures.atlas ? [] : [{ path: "atlas", element: <Atlas /> }]),
            ],
          },
          {
            path: "engagement-center",
            element: <EngagementCenterLayout />,
            children: [
              {
                index: true,
                element: hiddenFeatures.priorityChecklist ? (
                  <Navigate to="/app/engagement-center/health-summary" replace />
                ) : (
                  <Checklist />
                ),
              },
              { path: "health-summary", element: <HealthSummary /> },
              { path: "bookmarks", element: <Bookmarks /> },
              ...(hiddenFeatures.knowledgeCheck ? [] : [{ path: "knowledge-check", element: <KnowledgeCheck /> }]),
            ],
          },
        ],
      },
    ],
  },
  { path: "*", element: <NotFound /> },
]);
